// Bridges a program assignment as the API sends it and the CrudProgramModal form.
// No `@/` import: tested with `node --test`.

import { hasValue } from './helpers/numeric.js'

// The resource sends dates as "2026-09-29T00:00:00"; a date input only takes "2026-09-29".
const toDateInput = (value) => {
    if (typeof value !== 'string') return null
    const match = value.match(/^\d{4}-\d{2}-\d{2}/)
    return match ? match[0] : null
}

const toId = (value) => {
    if (!hasValue(value)) return null
    const number = Number(value)
    return Number.isInteger(number) ? number : value
}

// The horse an assignment belongs to: the one the Program resource nests in it (an older API sends
// no horse_id), else the horse_id of a form not saved yet. The form and the routes read it here.
export const programHorseId = (program) => toId(program?.horse?.id ?? program?.horse_id)

// Opens a saved assignment for editing, on its own horse (otherwise the form would pick the user's
// first horse) and, when the API does not send its protocol_id, on the page's protocol.
export const programToForm = (program, { protocolId = null, today = null } = {}) => {
    const vetCheckAt = toDateInput(program?.vet_check_at)

    return {
        ...program,
        horse_id: programHorseId(program),
        protocol_id: toId(program?.protocol_id ?? protocolId),
        start_at: toDateInput(program?.start_at) ?? program?.start_at ?? null,
        vet_check: vetCheckAt !== null,
        // Pre-filled with today, as for a new assignment, when the vet check is switched on.
        vet_check_at: vetCheckAt ?? today,
    }
}

// I5 "Chain": start date offered for the next assignment of a horse, the day after the
// current one ends. An assignment runs `dayCount` days from its start (day 1 = start_at),
// so the next one starts on start_at + dayCount. Pure calendar arithmetic (UTC), so the
// user's time zone never shifts the day. Null when the start date cannot be read.
export const nextAssignmentStart = (startAt, dayCount) => {
    const day = toDateInput(startAt)
    if (!day) return null

    const [year, month, date] = day.split('-').map(Number)
    const count = Number(dayCount)
    const days = Number.isFinite(count) && count > 0 ? Math.trunc(count) : 0
    const next = new Date(Date.UTC(year, month - 1, date + days))
    if (Number.isNaN(next.getTime())) return null

    return next.toISOString().slice(0, 10)
}

// The assignment's fields as POST and PUT horses/{horse}/programs take them. The horse is not in
// it: a create adds it, and the API does not move an assignment to another horse. A vet check
// switched off is sent as null, so it is never created and is removed on update.
export const programPayload = (form) => ({
    start_at: form?.start_at ?? null,
    protocol_id: toId(form?.protocol_id),
    reason: form?.reason ?? null,
    diagnostic: form?.diagnostic ?? null,
    vet_check_at: form?.vet_check ? (toDateInput(form?.vet_check_at) ?? form?.vet_check_at ?? null) : null,
})
